/** Rear / front fleet ID prefixes (e.g. Rear_1234A, Front_1234A). */
export function isRearDeviceId(deviceId: string): boolean {
  return /^rear_/i.test(String(deviceId ?? '').trim());
}

export function isFrontDeviceId(deviceId: string): boolean {
  return /^front_/i.test(String(deviceId ?? '').trim());
}

/** Derive paired front ID from a rear fleet ID (Rear_100A → Front_100A). */
export function pairedFrontIdFromRear(rearDeviceId: string): string | null {
  const rear = String(rearDeviceId ?? '').trim();
  if (!isRearDeviceId(rear)) return null;
  return `Front_${rear.slice(5)}`;
}

/**
 * Normalize status/MQTT device ids to the fleet table key (Rear_/Front_).
 * Bare sticker ids from legacy topics map to Rear_<sticker>.
 */
export function normalizeFleetStatusDeviceId(deviceId: string): string {
  const id = String(deviceId ?? '').trim();
  if (!id) return id;
  const rearMatch = /^rear_(.+)$/i.exec(id);
  if (rearMatch) return `Rear_${rearMatch[1]}`;
  const frontMatch = /^front_(.+)$/i.exec(id);
  if (frontMatch) return `Front_${frontMatch[1]}`;
  return `Rear_${id}`;
}

/** Keys that may hold latest status for a list row (canonical + legacy aliases). */
export function fleetStatusAliasIds(deviceId: string): string[] {
  const id = String(deviceId ?? '').trim();
  if (!id) return [];
  const canonical = normalizeFleetStatusDeviceId(id);
  const aliases = new Set<string>([id, canonical]);
  const rearSuffix = /^Rear_(.+)$/i.exec(canonical)?.[1];
  const frontSuffix = /^Front_(.+)$/i.exec(canonical)?.[1];
  if (rearSuffix) {
    aliases.add(rearSuffix);
    aliases.add(`Front_${rearSuffix}`);
  }
  if (frontSuffix) {
    aliases.add(frontSuffix);
    aliases.add(`Rear_${frontSuffix}`);
  }
  return [...aliases];
}

/** Coerce Dynamo/API race_mode (may arrive as number or string). */
export function isRaceModeActive(status?: { race_mode?: unknown }): boolean {
  const rm = status?.race_mode;
  if (rm == null || rm === '') return false;
  const n = Number(rm);
  return Number.isFinite(n) && n > 0;
}

/** True when the rear unit's latest status has race_mode set (authoritative for the pair). */
export function isRearRaceModeActive(
  statusMap: Record<string, { race_mode?: unknown } | undefined>,
  rearDeviceId: string,
): boolean {
  for (const key of fleetStatusAliasIds(rearDeviceId)) {
    if (isRaceModeActive(statusMap[key])) return true;
  }
  return false;
}

/** Front row is always offline while its rear is in race mode. */
export function isFrontForcedOfflineByRearRace(
  frontDeviceId: string,
  statusMap: Record<string, { race_mode?: unknown } | undefined>,
): boolean {
  if (!isFrontDeviceId(frontDeviceId)) return false;
  const rearId = pairedRearIdFromFront(frontDeviceId);
  if (!rearId) return false;
  return isRearRaceModeActive(statusMap, rearId);
}

export function pairedRearIdFromFront(frontDeviceId: string): string | null {
  const front = String(frontDeviceId ?? '').trim();
  if (!isFrontDeviceId(front)) return null;
  return `Rear_${front.slice(6)}`;
}

/**
 * For MQTT commands, fleet operations target the rear unit even when the UI
 * selection is a front device ID.
 */
export function commandTargetRearDeviceId(deviceId: string): string {
  const trimmed = String(deviceId ?? '').trim();
  if (!trimmed) return trimmed;
  const rear = pairedRearIdFromFront(trimmed);
  return rear ?? trimmed;
}

export function commandTargetRearDeviceIds(deviceIds: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of deviceIds) {
    const id = commandTargetRearDeviceId(String(raw ?? '').trim());
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

export type DeviceAssignmentRef = {
  deviceId: string;
  assignedAthleteId?: string;
};

export type ValidationReportDeviceGroup = {
  rearDeviceId: string;
  frontDeviceId: string;
};

/**
 * Group athlete assignments into rear rows (with optional paired front) and standalone
 * devices that are not front-only pair members.
 */
export function groupDevicesForValidationReport(devices: DeviceAssignmentRef[]): {
  groups: ValidationReportDeviceGroup[];
  standalone: DeviceAssignmentRef[];
} {
  const byAthlete = new Map<string, { rear?: string; front?: string; other: string[] }>();
  const standalone: DeviceAssignmentRef[] = [];

  for (const device of devices) {
    const did = String(device.deviceId ?? '').trim();
    if (!did) continue;
    const athleteId = String(device.assignedAthleteId ?? '').trim();
    if (!athleteId) {
      if (!isFrontDeviceId(did)) standalone.push({ deviceId: did, assignedAthleteId: device.assignedAthleteId });
      continue;
    }
    const entry = byAthlete.get(athleteId) ?? { other: [] };
    if (isRearDeviceId(did)) entry.rear = did;
    else if (isFrontDeviceId(did)) entry.front = did;
    else entry.other.push(did);
    byAthlete.set(athleteId, entry);
  }

  const groups: ValidationReportDeviceGroup[] = [];
  for (const [, pair] of byAthlete) {
    if (pair.rear) {
      groups.push({ rearDeviceId: pair.rear, frontDeviceId: String(pair.front ?? '').trim() });
    }
    for (const otherId of pair.other) {
      if (!isFrontDeviceId(otherId)) {
        standalone.push({ deviceId: otherId });
      }
    }
  }

  return { groups, standalone };
}
